import { Card, Row, Col, Badge } from "react-bootstrap";
import {
  Stars,
  Bullseye,
  GraphUpArrow,
  PatchCheckFill,
  Funnel,
} from "react-bootstrap-icons";

/**
 * RecommendationsGuide
 *
 * Dismissible explainer card that sits above the Top Recommendations list,
 * teaching students how the attempted-accuracy percentage and mastery
 * threshold work. Built with react-bootstrap + react-bootstrap-icons
 * (already in your dependency tree) — no extra packages required.
 *
 * Usage:
 *   <RecommendationsGuide />
 *   <TopRecommendations topics={...} />   // your existing component
 */

const ACCENT = "#3B5BFF"; // swap for your theme's primary if different from Bootstrap default

const STEPS = [
  {
    icon: Bullseye,
    title: "Only attempted work counts",
    body:
      "Each topic's percentage is your combined accuracy across the practice sets and tests you've completed. Unattempted resources are never part of the calculation.",
  },
  {
    icon: GraphUpArrow,
    title: "Example",
    body:
      "Attempted 4 sets and scored 82% correct across them? The topic shows 82% — even with 10 more sets still sitting unattempted.",
  },
  {
    icon: PatchCheckFill,
    title: "90% marks mastery",
    body:
      "Hit 90% attempted accuracy and the topic is considered mastered — it drops off this list automatically. Below that, keep practicing.",
  },
  {
    icon: Funnel,
    title: "Filters focus your time",
    body:
      "Priority orders topics by how close you are to mastering them — start with High to finish what's almost done. Type filters by Practice Sets or Tests, and Status filters by Attempted or Unattempted.",
  },
];

export default function RecommendationsGuide({ onDismiss, dismissible = true }) {
  return (
    <Card
      className="border-0 mb-4 overflow-hidden"
      style={{ borderRadius: "1rem", boxShadow: "0 1px 3px rgba(16, 24, 40, 0.06)" }}
    >
      <div style={{ height: 4, background: ACCENT }} />

      <Card.Body className="p-4 p-lg-5 position-relative">
        <div className="d-flex align-items-center gap-2 mb-2">
          <Stars size={16} style={{ color: ACCENT }} />
          <Badge
            bg="transparent"
            className="fw-semibold text-uppercase px-0"
            style={{ color: ACCENT, fontSize: "0.72rem", letterSpacing: "0.06em" }}
          >
            How this works
          </Badge>
        </div>

        <h2 className="fw-bold text-dark mb-2" style={{ fontSize: "1.6rem" }}>
          Understanding your recommendations
        </h2>
        <p className="text-secondary mb-4" style={{ maxWidth: "42rem", fontSize: "0.95rem" }}>
          This section highlights the topics that need more practice, based
          on how you've actually performed. Here's exactly how it's worked out.
        </p>

        <Row className="g-3">
          {STEPS.map(({ icon: Icon, title, body }, i) => (
            <Col xs={12} sm={6} key={title}>
              <div
                className="d-flex gap-3 h-100 p-3 rounded-3"
                style={{ background: "#F8F9FC", border: "1px solid #EEF0F6" }}
              >
                <div
                  className="d-flex align-items-center justify-content-center flex-shrink-0 rounded-3"
                  style={{
                    width: 40,
                    height: 40,
                    background: `${ACCENT}14`,
                  }}
                >
                  <Icon size={18} style={{ color: ACCENT }} />
                </div>
                <div className="min-w-0">
                  <h3 className="fw-semibold text-dark mb-1" style={{ fontSize: "0.92rem" }}>
                    {title}
                  </h3>
                  <p className="text-secondary mb-0" style={{ fontSize: "0.85rem", lineHeight: 1.5 }}>
                    {body}
                  </p>
                </div>
              </div>
            </Col>
          ))}
        </Row>

        <div
          className="d-flex align-items-center gap-2 mt-4 pt-3"
          style={{ borderTop: "1px solid #EEF0F6", fontSize: "0.85rem" }}
        >
          <span
            className="rounded-circle d-inline-block flex-shrink-0"
            style={{ width: 8, height: 8, background: ACCENT }}
          />
          <span className="text-secondary">
            Mastery threshold:{" "}
            <span className="fw-semibold text-dark">90% attempted accuracy</span>
          </span>
        </div>
      </Card.Body>
    </Card>
  );
}